import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnchancedModal } from 'src/components/Modals';
import useDocument from 'src/hooks/useDocument';
import type { IDocument } from 'src/interfaces/document';
import { addOrUpdateDocument } from 'src/modules/conversations/helpers/addOrUpdateDocument';
import type { TDocumentation } from 'src/modules/conversations/components/types';
import {
    getDocumentationContentType,
    getDocumentationMediaUrl,
} from 'src/views/OmniChannel/utils/documentation';
import { formatDocumentDescription } from 'src/modules/conversations/helpers/formatDocumentsDescription';
import type { TMultimediaPreview } from 'src/views/OmniChannel/utils/outboundMultimedia';
import { DocumentsGridLayout } from '../documents';
import { OutboundMultimediaPreviewModal } from '../multimedia/OutboundMultimediaPreviewModal';
import { DocumentationDocumentItem } from './DocumentationDocumentItem';

type TDocumentationModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    storeId?: string;
    documentation: TDocumentation[];
    setDocumentation: (documentation: TDocumentation[]) => void;
};

const toMultimediaPreviewFromDocument = (
    document: IDocument,
    t: (key: string) => string
): TMultimediaPreview => ({
    id: document._id,
    source: getDocumentationMediaUrl(document.file),
    name: formatDocumentDescription(document, t),
    contentType: getDocumentationContentType(document.file),
});

export const DocumentationModal = ({
    open,
    setOpen,
    storeId,
    documentation,
    setDocumentation,
}: TDocumentationModalProps) => {
    const { t } = useTranslation();
    const { documents, getDocumentsByStore, loading } = useDocument();
    const [previewDocument, setPreviewDocument] = useState<IDocument | null>(
        null
    );

    useEffect(() => {
        if (open && storeId) {
            getDocumentsByStore(storeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, storeId]);

    useEffect(() => {
        if (!open) {
            setPreviewDocument(null);
        }
    }, [open]);

    const documentCount = documents?.length ?? 0;

    const handleToggleDocument = (document: IDocument) => {
        const isSelected = documentation.some((item) => item._id === document._id);

        if (isSelected) {
            setDocumentation(
                documentation.filter((item) => item._id !== document._id)
            );
            return;
        }

        setDocumentation(addOrUpdateDocument(documentation, document, t));
    };

    const handlePreviewDocument = (document: IDocument) => {
        setPreviewDocument(document);
    };

    return (
        <>
            <EnchancedModal
                open={open}
                setOpen={setOpen}
                title={t('Buttons.SelectDocumentation')}
                maxWidth="md"
            >
                <DocumentsGridLayout
                    loading={loading}
                    documentCount={documentCount}
                    emptyMessage={t('Buttons.NoDocumentation')}
                >
                    {documents?.map((document: IDocument) => (
                        <DocumentationDocumentItem
                            key={document._id}
                            document={document}
                            documentation={documentation}
                            onToggle={handleToggleDocument}
                            onPreview={handlePreviewDocument}
                            t={t}
                        />
                    ))}
                </DocumentsGridLayout>
            </EnchancedModal>

            <OutboundMultimediaPreviewModal
                open={Boolean(previewDocument)}
                setOpen={(isOpen) => {
                    if (!isOpen) setPreviewDocument(null);
                }}
                preview={
                    previewDocument
                        ? toMultimediaPreviewFromDocument(previewDocument, t)
                        : null
                }
            />
        </>
    );
};
